<script lang="ts">
import { defineComponent, onMounted } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import {
  LOCAL_STORAGE_IID_KEY,
  LOCAL_STORAGE_REDIRECT_URI_KEY,
  PASSKEY_LOGIN_CANCELLED_PATH,
  PASSKEY_LOGIN_OUTCOME_QUERY,
  PASSKEY_LOGIN_CANCELLED_OUTCOME,
} from '@/constants'
import { isAllowedRedirectUri } from '@/services/trusted-device/is-allowed-redirect-uri'
import { router, useRoute } from '../../router'
import { useI18n } from '../../setup/setup-i18n'
import { redirectToUniversalLogin } from '../../services/auth/universal'
import { AuthPurpose } from '../../types'

const { useState: useLoginDomainStoreState, useGetters: useLoginDomainStoreGetters } = createNamespacedHelpers('LoginDomain')

export default defineComponent({

  name: 'PasskeyLoginPage',

  setup() {

    const { locale } = useI18n()

    const { tenant } = useLoginDomainStoreState(['tenant'])

    const { logo } = useLoginDomainStoreGetters(['logo'])

    const currentRoute = useRoute()

    const origin = `${window.location.protocol}//${window.location.host}`

    // this is the redirect uri from the app (e.g. the mobile callback)
    const getRedirectUri = (): string => {
      return currentRoute.value?.query.redirectUri as string
    }

    // the mobile install-id: from the query, falling back to the one already stored (as LoginPage)
    const getIID = (): string => {
      const iid = localStorage.getItem(LOCAL_STORAGE_IID_KEY)
      return currentRoute.value?.query.iid as string || iid
    }

    // AuthenticatePage reads the stored iid (sent to authx, and skips trusted device enrolment)
    const storeIID = () => {
      const iid = getIID()
      if (iid) {
        localStorage.setItem(LOCAL_STORAGE_IID_KEY, iid)
      } else {
        localStorage.removeItem(LOCAL_STORAGE_IID_KEY)
      }
    }

    // reached via Universal Login's back-to-login link (/login/passkey/cancelled). The route matches
    // case-insensitively (e.g. /login/passkey/CANCELLED), so compare the same way — otherwise a
    // cancel would re-launch the ceremony and drop the stored redirect uri
    const isCancelled = (): boolean => {
      const outcome = currentRoute.value?.params?.outcome
      return !!outcome && outcome.toLowerCase() === PASSKEY_LOGIN_CANCELLED_OUTCOME
    }

    // the user cancelled the passkey prompt in Universal Login: send them back to the app with a
    // cancelled outcome instead of re-triggering the ceremony
    const returnCancelledToApp = () => {

      const redirectUri = getRedirectUri() || localStorage.getItem(LOCAL_STORAGE_REDIRECT_URI_KEY)

      localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_KEY)

      // opened without an app redirect uri: there is no app to return to, so fall back to the
      // standard login page
      if (!redirectUri) {
        router.replace({ name: 'LoginPage' })
        return
      }

      if (!isAllowedRedirectUri(redirectUri)) {
        router.replace({ name: 'UnknownErrorPage' })
        return
      }

      const redirectUrl = new URL(redirectUri)
      redirectUrl.searchParams.set(PASSKEY_LOGIN_OUTCOME_QUERY, PASSKEY_LOGIN_CANCELLED_OUTCOME)

      window.location.assign(redirectUrl.toString())

    }

    const startPasskeyLogin = async () => {

      const redirectUri = getRedirectUri()

      // a redirect uri is optional, but one that is given must be allowed (open redirect protection)
      if (redirectUri && !isAllowedRedirectUri(redirectUri)) {
        // drop any stored redirect uri too: it is consumed later without re-validation
        // (determineRedirectUri), so a planted value must not outlive a rejected request
        localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_KEY)
        router.replace({ name: 'UnknownErrorPage' })
        return
      }

      if (redirectUri) {
        // honoured at the end of the chain by AuthenticatedPage (determineRedirectUri), and by the
        // cancel path below — the back-to-login link cannot carry it (see PASSKEY_LOGIN_CANCELLED_PATH)
        localStorage.setItem(LOCAL_STORAGE_REDIRECT_URI_KEY, redirectUri)
      } else {
        // no app redirect uri: drop any stale one so the login ends on the default redirect uri
        localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_KEY)
      }

      // Auth0 returns to /authenticate after the ceremony; AuthenticatedPage then sends the
      // code / tenantCode / userDeviceToken callback to the stored redirect uri (or the default)
      await redirectToUniversalLogin({
        tenant: tenant.value,
        redirectUri: `${origin}/authenticate`,
        prompt: 'login',
        temporaryMfaSetup: false,
        purpose: AuthPurpose.passkeyLogin,
        logoUri: logo.value,
        ui_locales: locale,
        backToLoginLink: `${origin}${PASSKEY_LOGIN_CANCELLED_PATH}`,
      })

    }

    onMounted(async () => {

      // Store the mobile install-id
      storeIID()

      try {

        if (isCancelled()) {
          returnCancelledToApp()
          return
        }

        await startPasskeyLogin()

      } catch {
        router.replace({ name: 'UnknownErrorPage' })
      }

    })

  },

})

</script>

<template>
  <div class="loading">
    <sm-loader />
  </div>
</template>

<style lang="scss" scoped>
.loading {
  @apply flex justify-center items-center min-h-screen;
}
</style>
